<template>
  <div>
    <v-card flat :key="card_key">
      <editor-tiptap
        :note.sync="note"
        :newnote.sync="new_note"
        :dirty.sync="dirty"
        :modifiedts="modified_ts"
        :notesize="note_bytes_form"
        :encrypted.sync="is_encrypted"
        :ing.sync="ing"
        :drafterror="draft_error"
        :saveerror="save_error"
        @saveNote="saveNote"
        @cancelDraft="openCancelDialog">
      </editor-tiptap>
    </v-card>

    <!-- Load modal -->
    <template>
      <v-dialog v-model="load_dialog" width="400px">
        <v-card>
          <v-card-title>{{ $t('proj_note.not_loaded') }} </v-card-title>
          <v-card-text>
            {{ $t('proj_note.not_loaded_description') }}
          </v-card-text>
          <v-card-actions class="actions">
            <v-spacer></v-spacer>
            <v-btn depressed small color="primary"
              @click="getProjNoteInfo()">{{ $t('proj_note.reload') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- Draft modal -->
    <template>
      <v-dialog v-model="draft_dialog" width="400px">
        <v-card>
          <v-card-title>{{ $t('proj_note.draft_not_saved') }}</v-card-title>
          <v-card-text>
            <div v-if="is_storable">
              {{ $t('proj_note.draft_not_saved_description') }}
            </div>
            <div v-else>
              {{ $t('proj_note.not_storable') }}
            </div>
          </v-card-text>
          <v-card-actions class="actions">
            <v-spacer></v-spacer>
            <v-btn depressed small color="primary"
              @click="draft_dialog=false;ing=false">
              {{ $t('proj_note.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- Save modal -->
    <template>
      <v-dialog v-model="save_dialog" width="400px">
        <v-card>
          <v-card-title>{{ $t('proj_note.note_not_saved') }}</v-card-title>
          <v-card-text>
            <div v-if="is_storable">
              {{ $t('proj_note.note_not_saved_description') }}
            </div>
            <div v-else>
              {{ $t('proj_note.not_storable') }}
            </div>
          </v-card-text>
          <v-card-actions class="actions">
            <v-spacer></v-spacer>
            <v-btn depressed small color="primary"
              @click="save_dialog=false;ing=false">
              {{ $t('proj_note.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- Crash modal -->
    <template>
      <v-dialog v-model="crash_dialog" width="400px">
        <v-card>
          <v-card-title>{{ $t('proj_note.note_crashed') }}</v-card-title>
          <v-card-text>
            {{ $t('proj_note.note_crashed_description') }}
          </v-card-text>
          <v-card-actions class="actions">
            <v-spacer></v-spacer>
            <v-btn depressed small color="primary"
              @click="crash_dialog=false">{{ $t('proj_note.confirm') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <!-- Cancel modal -->
    <template>
      <v-dialog scrollable v-model="cancel_dialog" max-width="800px">
        <v-card v-if="!!pre_note">
          <v-card-title>{{ $t('proj_note.cancel_note') }}</v-card-title>
          <v-card-text>
            <div v-html="JSON.parse(pre_note).data"></div>
          </v-card-text>
          <v-card-actions class="actions">
            <v-spacer></v-spacer>
            <v-btn text small
              @click="cancel_dialog=false">
              {{ $t('proj_note.cancel') }}
            </v-btn>
            <v-btn depressed small color="primary"
              @click="cancelDraft()">{{ $t('proj_note.confirm') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { formatBytes, toDatetime } from '../../../custom/function.js'

export default {
  data() {
    return {
      dirty: 0,

      pre_draft: null,
      pre_note: null,

      note: null,
      modified_ts: null,
      is_encrypted: null,

      card_key: 0,
      load_dialog: false,

      new_note: null,
      ing: false,

      note_bytes: 0,
      note_bytes_form: null,

      draft_error: false,
      draft_dialog: false,

      save_error: false,
      save_dialog: false,

      crash_dialog: false,
      crashed_note: null,

      cancel_dialog: false,
    }
  },

  computed: {
    is_storable() {
      return this.note_bytes < 2000000
    },

    proj_id() {
      return parseInt(this.$route.params.id)
    },
  },

  watch: {
    proj_id: {
      async handler(val) {
        if (!!val) {
          await this.getProjNoteInfo()
        } else {
          this.$store.commit('notes/resetProjNoteInfo')
          this.note = null
          this.dirty = 0
          this.initiate()
        }
      },
      immediate: true,
    },

    new_note(val) {
      let pre_draft_data = null
      if (!!val) {
        this.note_bytes = Buffer.from(val).length
        this.note_bytes_form = this.formatBytes(this.note_bytes)
        if (!!this.pre_draft) {
          pre_draft_data = JSON.parse(this.pre_draft).data
        }
        if (val!=pre_draft_data) {
          this.saveDraft()
        } else {
          this.ing = false
        }
      }
    },
  },

  methods: {
    formatBytes,
    toDatetime,

    initiate() {
      this.modified_ts = null
      this.is_encrypted = null
      this.ing = false
      this.note_bytes = 0
      this.note_bytes_form = null
      this.draft_error = false
      this.draft_dialog = false
      this.save_error = false
      this.save_dialog = false
      this.crashed_note = null
      this.crash_dialog = false
      this.cancel_dialog = false
    },

    async getProjNoteInfo() {
      let error = false
      try {
        await this.$store.dispatch('notes/getProjNoteInfo', this.proj_id)
      } catch(e) {
        error = true
      } finally {
        if (!error) {
          this.initiate()

          let note_info = this.$store.getters['notes/proj_note_info']
          this.dirty = note_info.dirty
          this.pre_draft = note_info.draft
          this.pre_note = note_info.note

          if (!!this.dirty) {
            if (!this.pre_draft) { this.note = null }
            else { this.parseNote(this.pre_draft) }
          } else {
            if (!this.pre_note) { this.note = null }
            else { this.parseNote(this.pre_note) }
          }
          this.card_key += 1
        }
        else {
          this.load_dialog = true
        }
      }
    },

    parseNote(note) {
      let parsed_note = JSON.parse(note)
      this.note = parsed_note.data
      this.modified_ts = this.toDatetime(new Date(parsed_note.modified_ts))
      this.is_encrypted = parsed_note.is_encrypted
    },

    async checkCrash() {
      let result = false
      let error = false
      try {
        await this.$store.dispatch('notes/getProjNoteInfo', this.proj_id)
      } catch(e) {
        this.$store.commit('error/setDialog', true)
        this.$store.commit('error/setErrorCode', e.response.status)
        error = true
        result = true
      } finally {
        if (!error) {
          let draft_obj = (this.$store.getters['notes/proj_note_info']).draft
          if (!this.crashed_note &&
            !!draft_obj&&(!this.pre_draft||(!!this.pre_draft&&(
              JSON.parse(this.pre_draft).modified_ts < JSON.parse(draft_obj).modified_ts
            )))) {
            this.crashed_note = this.getMergeNote(this.new_note, draft_obj)
            this.crash_dialog = true
            this.note = this.crashed_note
            this.card_key += 1
            // console.log('crashed_note', this.crashed_note)
            result = true
          }
        }
        return result
      }
    },

    getMergeNote(note, crashed_note_obj) {
      let parsed_crashed_note = JSON.parse(crashed_note_obj)
      return note + '<p>---' + 
        this.toDatetime(new Date(parsed_crashed_note.modified_ts))
         + '---</p>' + parsed_crashed_note.data
    },

    async saveDraft() {
      let error = false
      let saving_note_obj = {
        data: this.new_note,
        modified_ts: Date.now(),
        is_encrypted: this.is_encrypted
      }
      try {
        let crashed = await this.checkCrash()
        if (!crashed) {
          if (this.is_storable) {
            // console.log('saving_note_obj', saving_note_obj)
            try {
              await this.$store.dispatch('notes/saveProjNoteDraft', {
                proj_id: this.proj_id,
                draft: JSON.stringify(saving_note_obj),
              })
            } catch(e) {
              error = true
            } finally {
              if (!error) {
                this.pre_draft = JSON.stringify(saving_note_obj)
                this.modified_ts = this.toDatetime(new Date(saving_note_obj.modified_ts))
                this.ing = false
                this.dirty = 1
                this.crashed_note = null
                this.draft_error = false
                this.save_error = false
              }
            }
          } else {
            error = true
          }
        }
      } finally {
        this.draft_error = error
        if (this.draft_error) { this.draft_dialog = true }
      }
    },

    async saveNote() {
      let error = false
      let saving_note_obj = {
        data: this.new_note,
        modified_ts: Date.now(),
        is_encrypted: this.is_encrypted,
      }
      try {
        let crashed = await this.checkCrash()
        if (!crashed) {
          if (this.is_storable) {
            let note_words = this.countWords(this.new_note)
            try {
              await this.$store.dispatch('notes/saveProjNote', {
                proj_id: this.proj_id,
                note: JSON.stringify(saving_note_obj),
                note_wc: note_words,
              })
            } catch(e) {
              error = true
            } finally {
              if (!error) {
                await this.getProjNoteInfo()
              }
            }
          }
        }
      } finally {
        this.save_error = error
        if (this.save_error) { this.save_dialog = true}
      }
    },

    countWords(words) {
      let note = words.replace(/<[^>]*>/g," ")
      note = note.replace(/\s+/g, ' ')
      note = note.trim()
      let n = note.split(" ").length
      return n
    },

    async cancelDraft() {
      this.cancel_dialog = false
      let error = false
      try {
        await this.$store.dispatch('notes/cancelProjNoteDraft', 
          this.proj_id)
      } catch(e) {
        this.$store.commit('error/setDialog', true)
        this.$store.commit('error/setErrorCode', e.response.status)
        error = true
      } finally {
        if (!error) {
          await this.getProjNoteInfo()
        }
      }
    },

    openCancelDialog() {
      this.cancel_dialog = true
    },
  }
}
</script>

<style scoped>
  #footer {
    background-color: #fff;
    display: flex;
    flex-direction: row;
    margin: 0 24px;
    padding: 8px 15px;
    justify-content: space-between;
    align-items: center;
    border-top: .5px solid #ededed;
  }
</style>


// WEBPACK FOOTER //
// src/components/spa/project/Note.vue